import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, TextInput } from '@/components/ui/daisy';
import { PdfMaterialDraft } from '@/data/hook/book/usePdfMaterialDraft';

import PdfMaterialFileField from './PdfMaterialFileField';

export type PdfMaterialFormFields = {
  title: string;
};

type PdfMaterialFormProps = {
  draft: PdfMaterialDraft;
  defaultTitle?: string;
  isLoading?: boolean;
  onSubmit: (data: PdfMaterialFormFields) => void;
  onCancel: () => void;
  onRemoveFile: () => void;
};

export default function PdfMaterialForm({
  draft,
  defaultTitle = '',
  isLoading,
  onSubmit,
  onCancel,
  onRemoveFile,
}: PdfMaterialFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.materialsTab.pdfForm',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<PdfMaterialFormFields>({
    defaultValues: { title: defaultTitle },
  });
  const title = useWatch({ control, name: 'title' });

  const hasChanges = isDirty || draft.status === 'uploaded';
  const canSave = !!title.trim() && !!draft.attachment && hasChanges;

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <h3 className="font-semibold text-base-content leading-7">
        {t('title')}
      </h3>
      <TextInput.Fieldset
        label={t('titleLabel')}
        labelAlt={errors.title?.message}
      >
        <TextInput
          type="text"
          placeholder={t('titlePlaceholder')}
          className="w-full"
          color={errors.title ? 'error' : 'default'}
          {...register('title', { required: t('titleRequired') })}
        />
      </TextInput.Fieldset>
      <PdfMaterialFileField
        file={draft.file}
        attachment={draft.attachment}
        status={draft.status}
        progress={draft.progress}
        errorMessage={draft.errorMessage}
        onSelect={draft.start}
        onCancelUpload={draft.cancel}
        onRetry={draft.retry}
        onClear={draft.reset}
        onRemove={onRemoveFile}
      />
      <div className="relative flex self-end gap-4 w-94 max-w-full">
        <Button
          type="button"
          className="grow"
          onClick={onCancel}
          disabled={draft.isRemoving || isLoading}
        >
          {tCommon('cancel')}
        </Button>
        <Button
          type="submit"
          color="primary"
          className="grow"
          disabled={!canSave || isLoading}
          loading={isLoading}
        >
          {tCommon('save')}
        </Button>
      </div>
    </form>
  );
}
